import { DirectionProvider } from '@radix-ui/react-direction';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { useIsScribeEnableRtlEnabled } from '@/features/flags';
import { isRtlAppLocale } from '@heidi/utils/i18n';

interface AppDirectionProviderProps {
  children: ReactNode;
}

/**
 * Resolves text direction from the active intl locale and feeds it into Radix's
 * `DirectionProvider`, so primitives that consume `useDirection` (Slider,
 * DropdownMenu, ContextMenu, Menubar, NavigationMenu, Tabs, …) flip keyboard
 * handling and indicator positions for RTL locales.
 *
 * Must be mounted **inside** `<HeidiIntlProvider>` so it can read the resolved
 * active locale via `useIntl()` (a user account-language preference can differ
 * from the route `router.locale`; the intl provider is the single source of truth).
 *
 * The RTL flag is threaded into `isRtlAppLocale` because Arabic only resolves
 * through `mapLocaleAliasToSupportedLocale` when the flag is on — same gate the
 * intl provider uses, so an off flag still forces LTR.
 *
 * `document.documentElement.dir` / `lang` are synced in `HeidiIntlProvider`.
 */
export const AppDirectionProvider = ({ children }: AppDirectionProviderProps) => {
  const { locale } = useIntl();
  const isRtlLocaleFeatureEnabled = useIsScribeEnableRtlEnabled();
  const dir: 'rtl' | 'ltr' = isRtlAppLocale(locale, { isRtlLocaleFeatureEnabled })
    ? 'rtl'
    : 'ltr';

  return <DirectionProvider dir={dir}>{children}</DirectionProvider>;
};
